import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const Avances = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  const obrasData = [
    { id: 1, obra: 'Pozo XY-123', tipo: 'Perforación', avanceFisico: 78, avanceTiempo: 72, estado: 'En Tiempo', fechaFin: '15/04/2026' },
    { id: 2, obra: 'Pozo AB-456', tipo: 'Workover', avanceFisico: 45, avanceTiempo: 50, estado: 'En Tiempo', fechaFin: '28/03/2026' },
    { id: 3, obra: 'Workover MZ-789', tipo: 'Workover', avanceFisico: 92, avanceTiempo: 88, estado: 'Adelantado', fechaFin: '10/02/2026' },
    { id: 4, obra: 'Perforación NW-012', tipo: 'Perforación', avanceFisico: 18, avanceTiempo: 22, estado: 'Atrasado', fechaFin: '30/06/2026' },
  ];

  const curvaAvance = [
    { semana: 'S1', planificado: 10, real: 8 },
    { semana: 'S2', planificado: 22, real: 20 },
    { semana: 'S3', planificado: 35, real: 38 },
    { semana: 'S4', planificado: 50, real: 52 },
    { semana: 'S5', planificado: 65, real: 68 },
    { semana: 'S6', planificado: 78, real: 78 },
  ];

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'Adelantado': return <TrendingUpIcon color="success" />;
      case 'En Tiempo': return <CheckIcon color="info" />;
      case 'Atrasado': return <WarningIcon color="error" />;
      default: return null;
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'Adelantado': return 'success';
      case 'En Tiempo': return 'info';
      case 'Atrasado': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Avances de Obra
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Control de avance físico y temporal
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Registrar Avance
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="primary">5</Typography>
              <Typography variant="body2" color="text.secondary">Obras Activas</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="success.main">1</Typography>
              <Typography variant="body2" color="text.secondary">Adelantadas</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="info.main">2</Typography>
              <Typography variant="body2" color="text.secondary">En Tiempo</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="error.main">1</Typography>
              <Typography variant="body2" color="text.secondary">Atrasadas</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Curva de Avance */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Curva S - Pozo XY-123
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={curvaAvance}>
                  <defs>
                    <linearGradient id="colorPlan" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.grey[400]} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={theme.palette.grey[400]} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorReal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="semana" />
                  <YAxis />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Area type="monotone" dataKey="planificado" stroke={theme.palette.grey[400]} fill="url(#colorPlan)" strokeWidth={2} strokeDasharray="5 5" name="Planificado" />
                  <Area type="monotone" dataKey="real" stroke={theme.palette.success.main} fill="url(#colorReal)" strokeWidth={2} name="Real" />
                </AreaChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 20, height: 3, bgcolor: theme.palette.grey[400] }} />
                  <Typography variant="caption">Planificado</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 20, height: 3, bgcolor: theme.palette.success.main }} />
                  <Typography variant="caption">Real</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabla de Obras */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Estado de Obras
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Obra</TableCell>
                      <TableCell align="center">Avance</TableCell>
                      <TableCell>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {obrasData.map((obra) => (
                      <TableRow key={obra.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{obra.obra}</Typography>
                          <Typography variant="caption" color="text.secondary">{obra.tipo}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ width: 100 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption">{obra.avanceFisico}%</Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={obra.avanceFisico}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: alpha(theme.palette.grey[400], 0.2),
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getEstadoIcon(obra.estado)}
                            label={obra.estado}
                            size="small"
                            color={getEstadoColor(obra.estado)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Cards de Obras */}
        {obrasData.map((obra) => (
          <Grid item xs={12} md={6} key={obra.id}>
            <Card
              sx={{
                borderLeft: `4px solid ${
                  obra.estado === 'Adelantado'
                    ? theme.palette.success.main
                    : obra.estado === 'Atrasado'
                    ? theme.palette.error.main
                    : theme.palette.info.main
                }`,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>{obra.obra}</Typography>
                    <Typography variant="body2" color="text.secondary">{obra.tipo} • Fin: {obra.fechaFin}</Typography>
                  </Box>
                  <Chip
                    icon={getEstadoIcon(obra.estado)}
                    label={obra.estado}
                    size="small"
                    color={getEstadoColor(obra.estado)}
                  />
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Avance Físico</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={obra.avanceFisico}
                        sx={{ flex: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="body2" fontWeight={600}>{obra.avanceFisico}%</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Avance Temporal</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={obra.avanceTiempo}
                        sx={{ flex: 1, height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: theme.palette.secondary.main } }}
                      />
                      <Typography variant="body2" fontWeight={600}>{obra.avanceTiempo}%</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar Avance</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Obra</InputLabel>
                <Select label="Obra">
                  <MenuItem value="1">Pozo XY-123</MenuItem>
                  <MenuItem value="2">Pozo AB-456</MenuItem>
                  <MenuItem value="3">Workover MZ-789</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Avance Físico (%)" type="number" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Actividades Realizadas" multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observaciones" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Registrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Avances;
